import Cookies from 'universal-cookie';
import History from 'Store/history';
import LocalStorage from 'Utils/localStorage';
import { removeAuthToken } from 'Utils/authentication/setAxiosAuthHeader';

// Paths where force-auth failure should show the suspended-logout modal (not silent login redirect).
const SUSPENDED_LOGOUT_PATH_RE = /\/(sans-oyunlari|sanal-bahis|sanal-yaris|tjk)(\/|$)/;
const GAME_IFRAME_SELECTOR = '.chance-game-content iframe, #iframeContainer, .external-game-detail iframe';
const MODAL_SELECTOR = '.blyn-drawer.suspended-member-logout-modal';
const SSE_USER_LOGOUT = 1;

let suspendedMemberLogoutActive = false;
let closeWatcherId = null;
let historyUnblock = null;
let onSuspendedLogoutNavigate = null;
const cookies = new Cookies();

export const isSuspendedMemberLogoutActive = () => suspendedMemberLogoutActive;

export const setSuspendedMemberLogoutActive = active => {
  suspendedMemberLogoutActive = !!active;
};

/** Called when browser/app navigates while the suspended-logout popup is open. */
export const setSuspendedLogoutNavigateHandler = handler => {
  onSuspendedLogoutNavigate = typeof handler === 'function' ? handler : null;
};

export const isUserLogoutSSEEvent = message => {
  if (!message || typeof message !== 'object') return false;

  const candidates = [
    message,
    message.data,
    message.d,
    message.payload,
    message.body,
    message.d?.data,
    message.data?.data
  ];

  return candidates.some(candidate =>
    candidate
    && typeof candidate === 'object'
    && Number(candidate.mt) === SSE_USER_LOGOUT
  );
};

// SSE proxy returns HTTP 200 and forwards upstream auth failures as data events.
export const isSseUpstreamUnauthorizedEvent = message =>
  !!message
  && typeof message === 'object'
  && message.error === 'upstream_status'
  && Number(message.status) === 401;

export const hasActiveAuthSession = auth =>
  !!(auth?.isAuthenticated || auth?.data?.refreshToken || auth?.data?.userId);

// Only while a session remains on covered paths; after logout, 401 → login (not modal again).
export const shouldUseSuspendedLogoutOnForceAuthFailure = auth =>
  SUSPENDED_LOGOUT_PATH_RE.test(History.location?.pathname || '')
  && hasActiveAuthSession(auth);

const freezeGameIframes = () => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(GAME_IFRAME_SELECTOR).forEach(el => {
    el.style.pointerEvents = 'none';
  });
};

const unfreezeGameIframes = () => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(GAME_IFRAME_SELECTOR).forEach(el => {
    el.style.pointerEvents = '';
  });
};

const clearSessionWithoutRedux = () => {
  removeAuthToken();
  cookies.remove('user', { path: '/' });
  LocalStorage.remove('sortOptions');
  LocalStorage.remove('filterOptions');
  LocalStorage.remove('externalCustomerId');
};

const stopCloseWatcher = () => {
  if (closeWatcherId) {
    clearInterval(closeWatcherId);
    closeWatcherId = null;
  }
};

const stopHistoryGuard = () => {
  if (typeof historyUnblock === 'function') {
    historyUnblock();
  }
  historyUnblock = null;
};

const startHistoryGuard = () => {
  if (typeof window === 'undefined' || typeof History.block !== 'function') return;
  stopHistoryGuard();

  // Back/forward (and any transition) while popup is open → logout to homepage.
  historyUnblock = History.block(() => {
    stopHistoryGuard();
    const handler = onSuspendedLogoutNavigate;
    setTimeout(() => {
      if (typeof handler === 'function') {
        handler();
      } else {
        forceLogoutByReload();
      }
    }, 0);
    return false;
  });
};

const forceLogoutByReload = () => {
  stopCloseWatcher();
  stopHistoryGuard();
  unfreezeGameIframes();
  setSuspendedMemberLogoutActive(false);
  clearSessionWithoutRedux();
  if (typeof window !== 'undefined') {
    window.location.replace('/');
  }
};

const startCloseWatcher = () => {
  if (typeof window === 'undefined' || closeWatcherId) return;

  freezeGameIframes();

  closeWatcherId = setInterval(() => {
    if (!isSuspendedMemberLogoutActive()) {
      stopCloseWatcher();
      unfreezeGameIframes();
      return;
    }

    freezeGameIframes();

    if (!document.querySelector(MODAL_SELECTOR)) {
      forceLogoutByReload();
    }
  }, 300);
};

export const beginSuspendedMemberLogout = ({ openModal } = {}) => {
  if (!isSuspendedMemberLogoutActive()) {
    setSuspendedMemberLogoutActive(true);
    removeAuthToken();
  }
  if (typeof openModal === 'function') {
    openModal('suspendedMemberLogout');
  }
  startCloseWatcher();
  startHistoryGuard();
};

export const endSuspendedMemberLogout = () => {
  stopCloseWatcher();
  stopHistoryGuard();
  unfreezeGameIframes();
  setSuspendedMemberLogoutActive(false);
};
